import React, { useContext, useMemo, useState } from 'react';
import { getItemFromStore, removeItemFromStore, setItemToStore, isTokenExpired } from './helpers/utils';
import jwtDecode from 'jwt-decode';
import { isNil } from 'lodash';
import { v4 as uuidv4 } from 'uuid';

export const AuthContext = React.createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {

    const [authKey, setAuthKey] = useState(uuidv4());
    
    // Get the index of the currently selected account (defaults to 0)
    const getSelectedAccountIndex = () => {
        const savedIndex = getItemFromStore("selectedAccountIndex");
        if (savedIndex === null || savedIndex === undefined) {
            return 0;
        }
        const parsed = parseInt(savedIndex, 10);
        return isNaN(parsed) ? 0 : parsed;
    };

    const [selectedAccountIndex, setSelectedAccountIndex] = useState(getSelectedAccountIndex());
    
    const currentUser = useMemo(()=>{

        if (false){
            console.log(authKey);
        }

        const loggedInUser = getItemFromStore("login");

        if (isNil(loggedInUser)){
            return null;
        }

        // Safety check for Tokens array
        if (!loggedInUser.Tokens || !Array.isArray(loggedInUser.Tokens) || loggedInUser.Tokens.length === 0) {
            return null;
        }

        // Use the selected account index, or default to first token
        const index = Math.max(0, Math.min(selectedAccountIndex, loggedInUser.Tokens.length - 1));
        return loggedInUser.Tokens[index];
    },[authKey, selectedAccountIndex]);

    const getRoles = () => {
        let result = [];

        if (currentUser){
            result = jwtDecode(currentUser.Token)["http://schemas.microsoft.com/ws/2008/06/identity/claims/role"] || [];
        }

        return result;
    }

    const getSID = () => {
        let result = null;

        if (currentUser){
            result = jwtDecode(currentUser.Token)["http://schemas.xmlsoap.org/ws/2005/05/identity/claims/sid"];
        }

        return result;
    }

    const getUsername = () => {
        let result = null;

        if (currentUser){
            result = jwtDecode(currentUser.Token)["http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name"];
        }

        return result;
    }

    const isLoggedIn = () => {
        if (isNil(currentUser)) return false;
        try {
            return !isTokenExpired(currentUser.Token);
        } catch {
            return false;
        }
    }

    const logout = () => {
        removeItemFromStore("login");
        removeItemFromStore("selectedAccountIndex");
        window.location = "/auth/login";
    }

    const login = (data) => {
        setItemToStore("login", data);
        
        // Try to restore last used account for this user
        const username = data.Username;
        const savedAccountPrefs = getItemFromStore("accountPreferences");
        let accountIndex = 0;
        
        if (savedAccountPrefs && savedAccountPrefs[username] !== undefined) {
            const savedIndex = parseInt(savedAccountPrefs[username], 10);
            // Validate the saved index is within bounds
            if (!isNaN(savedIndex) && savedIndex >= 0 && savedIndex < data.Tokens.length) {
                accountIndex = savedIndex;
            }
        }
        
        setItemToStore("selectedAccountIndex", accountIndex);
        setSelectedAccountIndex(accountIndex);
        setAuthKey(uuidv4());
    }

    const setToken = (tok) => {
        const temp = { Token: tok };

        setItemToStore("login", {Tokens: [temp]});

        setAuthKey(uuidv4());
    }

    // Get all available accounts from stored tokens
    const getAvailableAccounts = () => {
        const loggedInUser = getItemFromStore("login");
        if (isNil(loggedInUser) || !loggedInUser.Tokens || !Array.isArray(loggedInUser.Tokens)) {
            return [];
        }
        return loggedInUser.Tokens.map((token, index) => {
            let roles = [];
            try {
                const decoded = jwtDecode(token.Token);
                const rolesClaim = decoded["http://schemas.microsoft.com/ws/2008/06/identity/claims/role"];
                roles = Array.isArray(rolesClaim) ? rolesClaim : (rolesClaim ? [rolesClaim] : []);
            } catch (e) {
                console.error("Error decoding token for account", token.AccountKey, e);
            }
            return {
                index,
                accountKey: token.AccountKey,
                hasAdminRole: token.HasAdminRole,
                hasGlobalAdminRole: token.HasGlobalAdminRole,
                roles: roles
            };
        });
    };

    // Switch to a different account
    const switchAccount = (index) => {
        const loggedInUser = getItemFromStore("login");
        if (isNil(loggedInUser) || !loggedInUser.Tokens || !Array.isArray(loggedInUser.Tokens) || index >= loggedInUser.Tokens.length) {
            return;
        }
        
        // Save account preference for this user
        const username = loggedInUser.Username;
        if (username) {
            const accountPrefs = getItemFromStore("accountPreferences") || {};
            accountPrefs[username] = index;
            setItemToStore("accountPreferences", accountPrefs);
        }
        
        // Update localStorage and immediately reload without updating React state
        // This prevents any API calls from happening with the wrong token during transition
        setItemToStore("selectedAccountIndex", index);
        window.location.reload();
    };

    const getCurrentAccountKey = () => {
        return currentUser ? currentUser.AccountKey : null;
    };

    return (
        <AuthContext.Provider
            value={{
                isLoggedIn: isLoggedIn(),
                token: currentUser ? currentUser.Token : null,
                setToken,
                roles: getRoles(),
                sid: getSID(),
                currentUser,
                username: getUsername(),
                isRaceTimer: isNil(getRoles()) ? false : getRoles().includes("RaceTimer") || getRoles().includes("Admin") || getRoles().includes("GlobalAdmin"),
                isCashier: isNil(getRoles()) ? false : getRoles().includes("Cashier") || getRoles().includes("Admin") || getRoles().includes("GlobalAdmin"),
                isAdmin: isNil(getRoles()) ? false : getRoles().includes("Admin") || getRoles().includes("GlobalAdmin"),
                isGlobalAdmin: isNil(getRoles()) ? false : getRoles().includes("GlobalAdmin"),
                logout,
                login,
                getAvailableAccounts,
                switchAccount,
                getCurrentAccountKey,
                selectedAccountIndex
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};